import { Add, Close, CloudUpload, Document } from '@carbon/icons-react';
import classNames from 'classnames';
import * as React from 'react';
import {
  Body,
  Button,
  Heading,
  Icon,
  Loader,
  LoadingIcon,
  OutlinedIconButton,
} from 'vet-bones/design-system/components';
import { usePortalFeatures } from 'vet-bones/utils/portal';

import { refreshDataSources } from 'src/actions/data-sources/action-creators/refreshDataSources';
import { DataSourceTypeGrid } from 'src/components/dataSourceSelection/DataSourceTypeGrid';
import { useDataSourceTypeList } from 'src/components/dataSourceSelection/hooks/useDataSourceTypeList';
import {
  Drawer,
  DrawerPosition,
  DrawerSize,
} from 'src/components/drawer/Drawer';
import { Ai } from 'src/components/icons/Ai';
import { Panel, PanelStack } from 'src/components/panelStack/PanelStack';
import { DialogFooter } from 'src/components/resourceDialog/DialogFooter';
import { DrawerHeaderTitle } from 'src/components/resourceDialog/DrawerHeaderTitle';
import { getDataSourceTableKey } from 'src/constants/dataSourceTable';
import {
  DataSourceType,
  SAMPLE_MUSIC_DATA_SOURCE_TYPE,
} from 'src/constants/dataSourceTypeDescription';
import { TEST_IDS } from 'src/constants/testIds';
import { DataConnectionPanelHeader } from 'src/containers/CreateProjectOverlay/components/DataConnectionPanelHeader';
import { useCreateProjectBase } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectBase';
import { useCreateProjectState } from 'src/containers/CreateProjectOverlay/hooks/useCreateProjectState';
import { useFileUploadState } from 'src/containers/CreateProjectOverlay/hooks/useFileUploadState';
import { useVgConfigState } from 'src/containers/CreateProjectOverlay/hooks/useVgConfigState';
import { ProjectResourceCard } from 'src/containers/ProjectNewResourceDialog/components/ProjectResourceCard';
import { VirtualGraphResourceCard } from 'src/containers/ProjectNewResourceDialog/components/VirtualGraphResourceCard';
import { ProjectResourceDialogContext } from 'src/containers/ProjectNewResourceDialog/context/useProjectResourceDialog';
import { createVgStateDefaults } from 'src/containers/ProjectNewResourceDialog/hooks/useVirtualGraphDialogState';
import { AddDataSourcePanel } from 'src/containers/ProjectNewResourceDialog/panels/AddDataSourcePanel';
import { FileUploadPanel } from 'src/containers/ProjectNewResourceDialog/panels/FileUploadPanel';
import { DataSourceCreationPanel } from 'src/containers/ProjectVirtualGraphPreviewDialog/panels/DataSourceCreationPanel';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { isRefreshingDataSourcesSelector } from 'src/selectors/isRefreshingDataSources';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';
import { useGenerateConcepts } from 'src/utils/voicebox/hooks/useGenerateConcepts';

import styles from 'src/components/resourceDialog/styles/panelStackDrawer.module.scss';

export const DataConnectionPanel: React.VFC = () => {
  const dispatch = useReduxDispatch();
  const {
    name,
    description,
    competencyQuestions,
    selectedFiles,
    fileContents,
    fileMetadata,
    setSelectedFiles,
    setFileContents,
    setFileMetadata,
    isAutoGeneratingModel,
    setIsAutoGeneratingModel,
    isAutoGeneratingMockData,
    setIsAutoGeneratingMockData,
    virtualGraphConfigs,
    setVirtualGraphConfigs,
  } = useCreateProjectState();
  const createProject = useCreateProjectBase();

  const generateConcepts = useGenerateConcepts();
  const [concepts, setConcepts] = React.useState<string[] | null>(null);
  const [isGeneratingConcepts, setIsGeneratingConcepts] = React.useState(false);
  const isVoiceboxEnabled = useIsVoiceboxEnabled();
  const portalFeatures = usePortalFeatures();
  const isVoiceboxSuggestionsEnabled =
    isVoiceboxEnabled && portalFeatures.voiceboxSuggestions;

  // Auto-fetch concepts on mount
  React.useEffect(() => {
    if (!isVoiceboxSuggestionsEnabled) {
      return undefined;
    }
    let cancelled = false;
    const fetchConcepts = async () => {
      setIsGeneratingConcepts(true);
      const result = await generateConcepts(
        name,
        description,
        competencyQuestions
          .filter((q) => q.question)
          .map((q) => ({ id: q.id, question: q.question }))
      );
      if (!cancelled) {
        setConcepts(result);
        setIsGeneratingConcepts(false);
      }
    };
    fetchConcepts();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data source related hooks and selectors
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const isRefreshingDataSources = useReduxSelector(
    isRefreshingDataSourcesSelector
  );
  const dataSourceTypes = useDataSourceTypeList();
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  // Ensure connection is set up in Redux state and connect if token is available
  useSetupDefaultPortalConnection({ connect: true });

  // Refresh data sources when panel mounts and user is connected
  React.useEffect(() => {
    if (isConnected) {
      dispatch(refreshDataSources());
    }
  }, [dispatch, isConnected]);

  const isLoadingDataSources = isConnecting || isRefreshingDataSources;

  const handleGenerateModel = React.useCallback(async () => {
    setIsAutoGeneratingModel(true);
    try {
      await createProject({ autoGenerateModel: true });
    } catch {
      setIsAutoGeneratingModel(false);
    }
  }, [createProject, setIsAutoGeneratingModel]);

  const handleGenerateMockData = React.useCallback(async () => {
    setIsAutoGeneratingMockData(true);
    try {
      await createProject({ autoGenerateMockData: true });
    } catch {
      setIsAutoGeneratingMockData(false);
    }
  }, [createProject, setIsAutoGeneratingMockData]);

  const [isAddDataDrawerOpen, setIsAddDataDrawerOpen] = React.useState(false);

  const [drawerFullSize, setDrawerFullSize] = React.useState(false);
  const [drawerTitle, setDrawerTitle] = React.useState(
    copy.components.projectResourceDialog.fileUploadPanel.title
  );
  const [headerBackButton, setHeaderBackButton] = React.useState<
    (() => void) | null
  >(null);

  const vgConfig = useVgConfigState({
    virtualGraphConfigs,
    setVirtualGraphConfigs,
    setIsAddDataDrawerOpen,
    setDrawerFullSize,
    setHeaderBackButton,
  });

  const fileUpload = useFileUploadState({
    setIsAddDataDrawerOpen,
    setDrawerFullSize,
    setDrawerTitle,
  });

  const totalResources = selectedFiles.length + vgConfig.totalVgResources;
  const hasResources = totalResources > 0;

  const { getRootProps, getInputProps, isDragActive } = fileUpload.mainDropzone;
  const rootProps = getRootProps() as React.HTMLAttributes<HTMLDivElement> & {
    ref: React.Ref<HTMLDivElement>;
  };
  const inputProps =
    getInputProps() as React.InputHTMLAttributes<HTMLInputElement> & {
      ref: React.Ref<HTMLInputElement>;
    };

  const {
    getRootProps: getAddDrawerRootProps,
    getInputProps: getAddDrawerInputProps,
    isDragActive: isAddDrawerDragActive,
  } = fileUpload.addDrawerDropzone;
  const addDrawerRootProps =
    getAddDrawerRootProps() as React.HTMLAttributes<HTMLDivElement> & {
      ref: React.Ref<HTMLDivElement>;
    };
  const addDrawerInputProps =
    getAddDrawerInputProps() as React.InputHTMLAttributes<HTMLInputElement> & {
      ref: React.Ref<HTMLInputElement>;
    };

  // Context for file preview drawer
  const fileDialogContextValue = React.useMemo(
    () => ({
      selectedFiles,
      setSelectedFiles,
      fileContents,
      setFileContents,
      fileMetadata,
      setFileMetadata,
      fileUploadOptions: {},
      setFileUploadOptions: () => {},
      fullSize: drawerFullSize,
      setFullSize: setDrawerFullSize,
      dialogTitle: drawerTitle,
      setDialogTitle: setDrawerTitle,
      headerBackButton: null,
      setHeaderBackButton: () => {},
      isLoading: false,
      setIsLoading: () => {},
      footerOptions: {},
      setFooterOptions: () => {},
      ...createVgStateDefaults(),
      resetDialogState: fileUpload.handleClosePreview,
    }),
    [
      selectedFiles,
      setSelectedFiles,
      fileContents,
      setFileContents,
      fileMetadata,
      setFileMetadata,
      drawerFullSize,
      drawerTitle,
      fileUpload.handleClosePreview,
    ]
  );

  // Context for VG config drawer - uses pending VG state
  const vgDialogContextValue = React.useMemo(
    () => ({
      selectedFiles: [],
      setSelectedFiles: () => {},
      fileContents: {},
      setFileContents: () => {},
      fileMetadata: {},
      setFileMetadata: () => {},
      fileUploadOptions: {},
      setFileUploadOptions: () => {},
      fullSize: drawerFullSize,
      setFullSize: setDrawerFullSize,
      dialogTitle: drawerTitle,
      setDialogTitle: setDrawerTitle,
      headerBackButton,
      setHeaderBackButton,
      isLoading: false,
      setIsLoading: () => {},
      footerOptions: vgConfig.vgFooterOptions,
      setFooterOptions: vgConfig.setVgFooterOptions,
      selectedDataSource: vgConfig.pendingVgDataSource,
      setSelectedDataSource: vgConfig.setPendingVgDataSource,
      selectedDataSourceType: vgConfig.pendingVgDataSourceType,
      setSelectedDataSourceType: vgConfig.setPendingVgDataSourceType,
      checkedVgTables: vgConfig.pendingVgCheckedTables,
      setCheckedVgTables: vgConfig.setPendingVgCheckedTables,
      vgTableMetadata: vgConfig.pendingVgTableMetadata,
      setVgTableMetadata: vgConfig.setPendingVgTableMetadata,
      customSqlQueries: vgConfig.pendingVgCustomSqlQueries,
      setCustomSqlQueries: vgConfig.setPendingVgCustomSqlQueries,
      vgPreviewItem: vgConfig.pendingVgPreviewItem,
      setVgPreviewItem: vgConfig.setPendingVgPreviewItem,
      virtualGraphOptions: vgConfig.pendingVgOptions,
      setVirtualGraphOptions: vgConfig.setPendingVgOptions,
      advancedVgOptions: vgConfig.pendingVgAdvancedOptions,
      setAdvancedVgOptions: vgConfig.setPendingVgAdvancedOptions,
      resetVgState: vgConfig.resetPendingVgState,
      resetDialogState: vgConfig.handleSaveAndCloseVgConfigDrawer,
    }),
    [
      headerBackButton,
      drawerFullSize,
      drawerTitle,
      setHeaderBackButton,
      vgConfig,
    ]
  );

  const handleBackButtonClick = React.useCallback(() => {
    if (fileUpload.previewFileIndex !== null) {
      fileUpload.handleClosePreview();
      return;
    }

    if (vgConfig.isVgConfigDrawerOpen && !drawerFullSize) {
      vgConfig.handleCloseVgConfigDrawer();
      return;
    }

    if (headerBackButton) {
      headerBackButton();
    } else if (vgConfig.isVgConfigDrawerOpen) {
      vgConfig.handleCloseVgConfigDrawer();
    } else {
      fileUpload.handleClosePreview();
    }
  }, [drawerFullSize, fileUpload, headerBackButton, vgConfig]);

  const handleClearAll = React.useCallback(() => {
    fileUpload.clearFiles();
    setVirtualGraphConfigs([]);
  }, [fileUpload, setVirtualGraphConfigs]);

  let vgBody: JSX.Element;
  if (!isConnected) {
    vgBody = (
      <div className="sd-data-connection-add-drawer-vg-message">
        <Body size="md">
          {
            copy.components.createProjectOverlay.dataConnectionPanel
              .addDataDrawer.vgNotConnected
          }
        </Body>
      </div>
    );
  } else if (isLoadingDataSources) {
    vgBody = (
      <div className="sd-data-connection-add-drawer-vg-message">
        <Icon IconComponent={LoadingIcon} size="sm" />
        <Body size="md">
          {
            copy.components.createProjectOverlay.dataConnectionPanel
              .addDataDrawer.vgLoading
          }
        </Body>
      </div>
    );
  } else {
    vgBody = (
      <div
        data-testid={
          TEST_IDS.createProjectOverlay.dataConnectionPanel.dataSourceTypeGrid
        }
      >
        <DataSourceTypeGrid
          dataSourceTypes={dataSourceTypes}
          isSampleLoading={vgConfig.isSampleDataSourceLoading}
          onSelectType={vgConfig.handleSelectDataSourceType}
        />
      </div>
    );
  }

  let panelBody: JSX.Element;
  if (isAutoGeneratingModel || isAutoGeneratingMockData) {
    panelBody = (
      <div className="sd-data-connection-panel-content">
        <div className="sd-data-connection-panel-generating">
          <Loader complete={false} />
          <Heading size="md">
            {
              copy.components.createProjectOverlay.dataConnectionPanel
                .generatingModel
            }
          </Heading>
        </div>
      </div>
    );
  } else if (!hasResources) {
    panelBody = (
      <>
        <div className="sd-data-connection-panel-content">
          <div
            ref={rootProps.ref}
            className={classNames('sd-data-connection-panel-dropzone', {
              'sd-data-connection-panel-dropzone--active': isDragActive,
            })}
            data-testid={
              TEST_IDS.createProjectOverlay.dataConnectionPanel.dropzone
            }
            onClick={rootProps.onClick}
            onDragEnter={rootProps.onDragEnter}
            onDragLeave={rootProps.onDragLeave}
            onDragOver={rootProps.onDragOver}
            onDrop={rootProps.onDrop}
            role="button"
            tabIndex={0}
          >
            <input
              ref={inputProps.ref}
              accept={inputProps.accept}
              data-testid={TEST_IDS.dropzone.fileInput}
              multiple={inputProps.multiple}
              onChange={inputProps.onChange}
              style={inputProps.style}
              type={inputProps.type}
            />
            <div className="sd-data-connection-panel-dropzone-icon">
              <Icon IconComponent={CloudUpload} size="xl" />
            </div>
            <Body className="sd-data-connection-panel-dropzone-text" size="md">
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .dropzone.dragText
              }
            </Body>
            <Body
              className="sd-data-connection-panel-dropzone-support"
              size="sm"
            >
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .dropzone.supportText
              }
            </Body>
          </div>

          <div
            className="sd-data-connection-panel-vg-section"
            data-testid={
              TEST_IDS.createProjectOverlay.dataConnectionPanel.vgSection
            }
          >
            <Heading size="md">
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .addDataDrawer.vgSection
              }
            </Heading>
            {vgBody}
          </div>
        </div>

        <div className="sd-create-project-panel-footer">
          <div className="sd-create-project-panel-footer-actions" />
          <div className="sd-create-project-panel-footer-actions">
            <Button
              onClick={() => createProject()}
              size="lg"
              testId={
                TEST_IDS.createProjectOverlay.dataConnectionPanel
                  .generateModelButton
              }
              variant="primary-accent"
            >
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .generateModel
              }
            </Button>
          </div>
        </div>
      </>
    );
  } else {
    panelBody = (
      <>
        <div className="sd-data-connection-panel-resources">
          <div className="sd-create-project-panel-list-title">
            <div className="sd-create-project-panel-list-title-text">
              <Heading size="md">
                {
                  copy.components.createProjectOverlay.dataConnectionPanel
                    .myResources
                }
              </Heading>
              <Heading
                size="md"
                testId={
                  TEST_IDS.createProjectOverlay.dataConnectionPanel
                    .resourcesCount
                }
              >
                {totalResources}
              </Heading>
            </div>

            <div className="sd-create-project-panel-list-title-actions">
              <OutlinedIconButton
                disabled={isAutoGeneratingModel}
                elevated
                leftIcon={Close}
                onClick={handleClearAll}
                testId={
                  TEST_IDS.createProjectOverlay.dataConnectionPanel
                    .clearAllButton
                }
              />
            </div>
          </div>

          <div className="sd-data-connection-panel-resources-scroll">
            <div className="sd-data-connection-panel-files">
              {selectedFiles.map((file, index) => {
                const fileKey = fileUpload.getFileKey(file);
                const isPartial = fileMetadata[index]?.isPartial;
                return (
                  <ProjectResourceCard
                    key={fileKey}
                    fileId={fileKey}
                    index={index}
                    metadata={fileMetadata[index]}
                    onRemove={fileUpload.handleRemoveFile}
                    onViewPreview={fileUpload.handleViewPreview}
                    resourceIcon={Document}
                    resourceName={
                      fileMetadata[index]?.fileName ||
                      file.name.replace(/\.csv$/i, '')
                    }
                    validationError={
                      isPartial ? copy.notifications.csv.partial : undefined
                    }
                  />
                );
              })}

              {virtualGraphConfigs.map((config) => (
                <React.Fragment key={config.id}>
                  {config.checkedTables.map((table) => {
                    const tableKey = getDataSourceTableKey(table);
                    const metadata = config.tableMetadata[tableKey];
                    const displayName = metadata?.editedName || table.name;
                    const displayFields = metadata?.fields
                      ? metadata.fields.map((field) => ({
                          ...field,
                          label:
                            metadata.editedFieldLabels?.[field.value] ??
                            field.label,
                        }))
                      : [];
                    const displayMetadata = metadata
                      ? { ...metadata, fields: displayFields }
                      : undefined;

                    return (
                      <VirtualGraphResourceCard
                        key={`${config.id}-table-${tableKey}`}
                        dataSourceType={config.dataSourceType}
                        metadata={displayMetadata}
                        name={displayName}
                        onRemove={() =>
                          vgConfig.handleRemoveVgTable(config.id, tableKey)
                        }
                        onViewPreview={() =>
                          vgConfig.handleViewVgTablePreview(config, tableKey)
                        }
                      />
                    );
                  })}
                  {config.customSqlQueries.map((query) => (
                    <VirtualGraphResourceCard
                      key={`${config.id}-query-${query.name}`}
                      dataSourceType={config.dataSourceType}
                      metadata={
                        query.fields
                          ? {
                              tableName: query.name,
                              fields: query.fields,
                              rowCount: query.rowCount ?? query.fields.length,
                            }
                          : undefined
                      }
                      name={query.name}
                      onRemove={() =>
                        vgConfig.handleRemoveVgQuery(config.id, query.name)
                      }
                      onViewPreview={() =>
                        vgConfig.handleViewVgQueryPreview(config, query.name)
                      }
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="sd-create-project-panel-footer">
          <div className="sd-create-project-panel-footer-actions">
            <Button
              disabled={isAutoGeneratingModel}
              onClick={() => setIsAddDataDrawerOpen(true)}
              rightIcon={Add}
              size="lg"
              testId={
                TEST_IDS.createProjectOverlay.dataConnectionPanel.addDataButton
              }
              variant="secondary-transparent-gray"
            >
              {copy.components.createProjectOverlay.dataConnectionPanel.addData}
            </Button>
          </div>
          <div className="sd-create-project-panel-footer-actions">
            <Button
              disabled={isAutoGeneratingModel}
              leftIcon={isAutoGeneratingModel ? LoadingIcon : undefined}
              onClick={handleGenerateModel}
              size="lg"
              testId={
                TEST_IDS.createProjectOverlay.dataConnectionPanel
                  .generateModelButton
              }
              variant="primary-accent"
            >
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .generateModel
              }
            </Button>
          </div>
        </div>
      </>
    );
  }

  // Get initial panel for VG config drawer
  const getVgInitialPanel = React.useCallback((): Panel => {
    const dataSourceType = vgConfig.pendingVgDataSourceType as DataSourceType;
    const dataSources = dataSourcesByType[dataSourceType] || [];
    const isSampleMusic = dataSourceType === SAMPLE_MUSIC_DATA_SOURCE_TYPE;

    if (vgConfig.pendingVgDataSource || isSampleMusic) {
      return {
        props: {
          dataSourceType,
          skipToTables: true,
          dataSourceId: vgConfig.pendingVgDataSource?.id,
          initialPreviewTable:
            vgConfig.pendingVgPreviewItem?.type === 'table'
              ? vgConfig.pendingVgPreviewItem.name
              : undefined,
          initialCustomQuery:
            vgConfig.pendingVgPreviewItem?.type === 'query'
              ? vgConfig.pendingVgPreviewItem.name
              : undefined,
        },
        renderPanel: AddDataSourcePanel,
      } as Panel;
    }

    if (dataSources.length === 0) {
      return {
        props: {
          dataSourceType,
          contextOverrides: {
            setDialogTitle: setDrawerTitle,
            setHeaderBackButton,
            setFullSize: setDrawerFullSize,
            setFooterOptions: vgConfig.setVgFooterOptions,
          },
        },
        renderPanel: DataSourceCreationPanel,
      } as Panel;
    }

    return {
      props: { dataSourceType },
      renderPanel: AddDataSourcePanel,
    } as Panel;
  }, [
    vgConfig,
    dataSourcesByType,
    setDrawerTitle,
    setHeaderBackButton,
    setDrawerFullSize,
  ]);

  const isGenerating = isAutoGeneratingModel || isAutoGeneratingMockData;

  return (
    <div className="sd-data-connection-panel">
      {!isGenerating ? (
        <>
          {isVoiceboxSuggestionsEnabled ? (
            <DataConnectionPanelHeader
              concepts={concepts}
              isGeneratingConcepts={isGeneratingConcepts}
            />
          ) : null}

          {!hasResources && isVoiceboxSuggestionsEnabled ? (
            <div className="sd-data-connection-panel-mock-data">
              <Button
                leftIcon={Ai}
                onClick={handleGenerateMockData}
                size="lg"
                testId={
                  TEST_IDS.createProjectOverlay.dataConnectionPanel
                    .generateMockDataButton
                }
                variant="primary-accent"
              >
                {
                  copy.components.createProjectOverlay.dataConnectionPanel
                    .generateMockData
                }
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {panelBody}

      <ProjectResourceDialogContext.Provider value={fileDialogContextValue}>
        <Drawer
          autoFocus={false}
          canEscapeKeyClose
          canOutsideClickClose={false}
          className={styles.drawerProjectResource}
          enforceFocus={false}
          hasBackdrop
          isCloseButtonShown={false}
          isOpen={fileUpload.previewFileIndex !== null}
          onClose={fileUpload.handleClosePreview}
          position={DrawerPosition.RIGHT}
          size={drawerFullSize ? DrawerSize.FULL : DrawerSize.MD}
          title={
            <DrawerHeaderTitle
              onBack={handleBackButtonClick}
              title={drawerTitle}
            />
          }
        >
          <div className={styles.drawerBody}>
            {fileUpload.previewFileIndex !== null ? (
              <FileUploadPanel
                closePanel={fileUpload.handleClosePreview}
                fileIndex={fileUpload.previewFileIndex}
              />
            ) : null}
          </div>
        </Drawer>
      </ProjectResourceDialogContext.Provider>

      <Drawer
        canEscapeKeyClose
        canOutsideClickClose
        className={styles.drawerProjectResource}
        data-testid={
          TEST_IDS.createProjectOverlay.dataConnectionPanel.addDataDrawer
        }
        hasBackdrop
        isOpen={isAddDataDrawerOpen}
        onClose={() => setIsAddDataDrawerOpen(false)}
        position={DrawerPosition.RIGHT}
        size={DrawerSize.MD}
        title={
          <DrawerHeaderTitle
            title={
              copy.components.createProjectOverlay.dataConnectionPanel
                .addDataDrawer.title
            }
          />
        }
      >
        <div className={styles.drawerBody}>
          <div className="sd-data-connection-add-drawer-section">
            <Heading size="sm">
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .addDataDrawer.csvSection
              }
            </Heading>
            <div
              ref={addDrawerRootProps.ref}
              className={classNames('sd-data-connection-panel-dropzone', {
                'sd-data-connection-panel-dropzone--active':
                  isAddDrawerDragActive,
              })}
              data-testid={
                TEST_IDS.createProjectOverlay.dataConnectionPanel
                  .addDataDrawerDropzone
              }
              onClick={addDrawerRootProps.onClick}
              onDragEnter={addDrawerRootProps.onDragEnter}
              onDragLeave={addDrawerRootProps.onDragLeave}
              onDragOver={addDrawerRootProps.onDragOver}
              onDrop={addDrawerRootProps.onDrop}
              role="button"
              tabIndex={0}
            >
              <input
                ref={addDrawerInputProps.ref}
                accept={addDrawerInputProps.accept}
                data-testid={TEST_IDS.dropzone.fileInput}
                multiple={addDrawerInputProps.multiple}
                onChange={addDrawerInputProps.onChange}
                style={addDrawerInputProps.style}
                type={addDrawerInputProps.type}
              />
              <div className="sd-data-connection-panel-dropzone-icon">
                <Icon IconComponent={CloudUpload} size="xl" />
              </div>
              <Body
                className="sd-data-connection-panel-dropzone-text"
                size="md"
              >
                {
                  copy.components.createProjectOverlay.dataConnectionPanel
                    .dropzone.dragText
                }
              </Body>
              <Body
                className="sd-data-connection-panel-dropzone-support"
                size="sm"
              >
                {
                  copy.components.createProjectOverlay.dataConnectionPanel
                    .dropzone.supportText
                }
              </Body>
            </div>
          </div>

          <div
            className="sd-data-connection-add-drawer-section"
            data-testid={
              TEST_IDS.createProjectOverlay.dataConnectionPanel.vgSection
            }
          >
            <Heading size="sm">
              {
                copy.components.createProjectOverlay.dataConnectionPanel
                  .addDataDrawer.vgSection
              }
            </Heading>
            {vgBody}
          </div>
        </div>
      </Drawer>

      <ProjectResourceDialogContext.Provider value={vgDialogContextValue}>
        <Drawer
          autoFocus={false}
          canEscapeKeyClose
          canOutsideClickClose={false}
          className={styles.drawerProjectResource}
          data-testid={
            TEST_IDS.createProjectOverlay.dataConnectionPanel.vgConfigDrawer
          }
          enforceFocus={false}
          hasBackdrop
          isCloseButtonShown={false}
          isOpen={vgConfig.isVgConfigDrawerOpen}
          onClose={vgConfig.handleCloseVgConfigDrawer}
          position={DrawerPosition.RIGHT}
          size={drawerFullSize ? DrawerSize.FULL : DrawerSize.MD}
          title={
            <DrawerHeaderTitle
              onBack={handleBackButtonClick}
              title={drawerTitle}
            />
          }
        >
          <div className={styles.drawerBody}>
            {vgConfig.isVgConfigDrawerOpen &&
            vgConfig.pendingVgDataSourceType ? (
              <PanelStack
                initialPanel={getVgInitialPanel()}
                renderActivePanelOnly
              />
            ) : null}
          </div>
          {vgConfig.vgFooterOptions.onNext ? (
            <div className={styles.drawerFooter}>
              <DialogFooter
                footerOptions={{
                  onCancel: vgConfig.handleCloseVgConfigDrawer,
                  onNext: vgConfig.handleAddVgButtonClick,
                  isNextDisabled: vgConfig.vgFooterOptions.isNextDisabled,
                  nextLabel:
                    vgConfig.vgFooterOptions.nextLabel ||
                    copy.components.projectResourceDialog.addDataSourcePanel
                      .addButton,
                }}
              />
            </div>
          ) : null}
        </Drawer>
      </ProjectResourceDialogContext.Provider>
    </div>
  );
};
